import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NzModalModule, NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { CalendarEventsService } from 'src/app/services/calendarEvents.service';
import { Subject, takeUntil } from 'rxjs';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSwitchModule } from 'ng-zorro-antd/switch';

import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { EventApi, EventInput } from '@fullcalendar/core';
import { IPersonel } from 'src/app/interfaces/Appointment/IAppointmentPersonel';
import { AppointmentService } from 'src/app/services/appointment.service';
import {
  IRolePersonel,
  ISubRolePersonel,
} from 'src/app/interfaces/Appointment/IAppointmentRoles';
import {
  IAppointmentReason,
  IAppointmentServiceType,
  IAppointmentStatus,
} from 'src/app/interfaces/Appointment/IAppointmentDropdownData';
import { MatIconModule } from '@angular/material/icon';
import { en_GB, NzI18nService } from 'ng-zorro-antd/i18n';
import { mapCalendarEventsToBackendAppointmentData } from '../event-utils';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { IAppointmentOverlapsData } from 'src/app/interfaces/Appointment/IAppointmentOverlaps';
import Swal from 'sweetalert2';
import { COLORS } from 'src/styles/colors';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { nz_date_format } from 'src/date/date.configuration';
import { SwalToastService } from 'src/app/services/swal.service';

@Component({
  selector: 'app-add-calendar-event',
  standalone: true,
  templateUrl: './add-calendar-event.component.html',
  styleUrls: ['./add-calendar-event.component.css'],
  imports: [
    NzModalModule,
    NzSelectModule,
    NzInputModule,
    NzSwitchModule,
    FormsModule,
    ReactiveFormsModule,
    NzButtonModule,
    TranslateModule,
    FlexLayoutModule,
    NzDatePickerModule,
    MatIconModule,
    NzDividerModule,
    NzTooltipModule,
    NzIconModule,
  ],
})
export class AddCalendarEventComponent implements OnInit, OnDestroy {
  @ViewChild('modalRef', { static: true }) modalRef!: NzModalRef;

  private destroy$ = new Subject<void>();
  isLoading = false;

  // Dynamic resources (patients)
  patientsList: any[] = [];
  isPatientOptionsLoading = false;
  patientOptionsLoadFailed = false;
  private allowedPatientIds = new Set<string>();
  private patientRequestVersion = 0;
  departmentList: any[] = [];

  rolesList: IRolePersonel[] = []; // Store full role data with users and sub-roles
  specialtiesList: ISubRolePersonel[] = []; // Store full sub-role data separately
  personnelList: IPersonel[] = []; // Store full personnel data
  recurrenceTypeList: any[] = []; // Store full recurrence type data

  filteredPersonnelList: IPersonel[] = []; // Stores filtered personnel
  filteredSpecialtiesList: ISubRolePersonel[] = []; // Stores filtered sub-roles based on selected roles

  isSubRoleEnabled: boolean = false; // Track if sub-role dropdown should be active

  appointmentStatusList: IAppointmentStatus[] = [];
  appointmentServiceTypeList: IAppointmentServiceType[] = [];
  appointmentReasonsList: IAppointmentReason[] = [];
  slotStatusList: IAppointmentStatus[] = [];
  participantStatusList: IAppointmentStatus[] = [];

  timeOptions: string[] = this.generateTimeOptions();

  isEditingRecurring: boolean = false;

  startTimeVisible: boolean = true;
  endTimeVisible: boolean = true;

  showDiscardConfirm = false;

  isTitleEditing = false;
  isTitleManuallyEdited = false;
  titleTooltipVisible = false;
  private isAutoTitleEnabled = false;
  private committedTitle = '';

  colorOptions = [
    { name: 'red', value: 'rgba(201, 105, 77, 0.4)' },
    { name: 'yellow', value: 'rgba(216, 201, 111, 0.4)' },
    { name: 'blue', value: 'rgba(128, 186, 226, 0.4)' },
    { name: 'green', value: 'rgba(13, 148, 136, 0.4)' },
    { name: 'purple', value: 'rgba(149, 128, 226, 0.4)' },
  ];

  colorOpacity: { [key: string]: number } = {
    red: 1,
    yellow: 1,
    blue: 1,
    green: 1,
    purple: 1,
  };

  nz_date_format = nz_date_format;

  // Validator to check if start date is less than end date
  startLessThanEndValidator = (
    formGroup: FormGroup
  ): { [key: string]: any } | null => {
    const startDate = formGroup.get('startDate')?.value;
    const startTime = formGroup.get('startTime')?.value;
    const endTime = formGroup.get('endTime')?.value;
    const allDay = formGroup.get('allDay')?.value;

    if (!startDate || !startTime || !endTime || allDay) return null;

    const start = new Date(`${startDate}T${startTime}`);
    const end = new Date(`${startDate}T${endTime}`);

    return start >= end ? { startAfterEnd: true } : null;
  };

  // Validator to ensure either 'allDay' is true or 'end' date is provided
  requireAllDayOrEndValidator = (
    formGroup: FormGroup
  ): { [key: string]: any } | null => {
    const allDay = formGroup.get('allDay')?.value;
    const endTime = formGroup.get('endTime')?.value;

    if (!allDay && !endTime) {
      return { endDateOrAllDayRequired: true };
    }
    return null;
  };

  recurrenceValidator = (
    formGroup: FormGroup
  ): { [key: string]: any } | null => {
    const frequency = formGroup.get('recurrenceFrequency')?.value;
    const occurrences = formGroup.get('recurrenceOccurrences')?.value;

    const limits: { [key: number]: number } = {
      2: 365, // Daily
      3: 52, // Weekly
      4: 24, // Monthly
      5: 10, // Yearly
    };

    if (frequency === 1) return null; // Does not repeat

    // ✅ Ensure integer
    if (!Number.isInteger(occurrences)) {
      return { recurrenceOccurrencesRequired: true };
    }

    if (!occurrences || occurrences < 1) {
      return { recurrenceOccurrencesRequired: true };
    }

    const max = limits[frequency] || 100;
    if (occurrences > max) {
      return { recurrenceOccurrencesExceeded: { max } };
    }

    return null;
  };

  eventForm: FormGroup = new FormGroup({});

  editingEvent: EventApi | EventInput | null = null;
  private preselectedPersonnelIds: string[] = [];
  private isPatchingEditParticipants = false;
  private initialFormState = '';

  isVisible = false;

  constructor(
    private fb: FormBuilder,
    private calendarEventService: CalendarEventsService,
    private modalService: NzModalService,
    private appointmentService: AppointmentService,
    private translate: TranslateService,
    private readonly nz: NzI18nService,
    private readonly swalToastService: SwalToastService
  ) {
    this.nz.setLocale(en_GB);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.modalRef.destroy();
  }

  ngOnInit(): void {
    this.eventForm = this.fb.group(
      {
        id: [0],
        title: [null, [Validators.required]],
        description: null,
        department: [null /*[Validators.required]*/],
        patients: [null], // Required field for patients
        personnel: [[], [Validators.required]],
        roles: [[], [Validators.required]],
        specialties: [[]],
        // start: [null, [Validators.required]],
        // end: null,
        startDate: [null, Validators.required],
        startTime: [null, Validators.required],
        // endDate: [null, Validators.required],
        endTime: [null, Validators.required],

        allDay: false,
        eventColor: [
          {
            value: this.colorOptions.find((color) => color.name === 'blue')
              ?.value,
            disabled: true,
          },
          Validators.required,
        ],
        // Status fields
        appointmentStatusId: [null, [Validators.required]], // when we implement status logic then uncomment this -> // [null, [Validators.required]],
        appointmentServiceTypeId: [null, [Validators.required]],
        appointmentReasonId: [null, [Validators.required]],
        slotStatus: [21], // when we implement status logic then uncomment this -> // [null, [Validators.required]],
        slotId: [0],
        personnelStatus: [30], // when we implement status logic then uncomment this -> // [null, [Validators.required]],
        patientStatus: [30], // when we implement status logic then uncomment this -> // [null, [Validators.required]],
        departmentStatus: [30], // when we implement status logic then uncomment this -> // [null, [Validators.required]],
        recurrenceFrequency: [1], // default to 'Does not repeat' (id: 1)
        recurrenceOccurrences: [null, [Validators.min(1), Validators.max(365)]],
        updateOption: 3, // Default to "All slots" (id: 3) // Revert to 1
      },
      {
        validators: [
          this.startLessThanEndValidator,
          this.requireAllDayOrEndValidator,
          this.recurrenceValidator,
        ],
      }
    );
    this.eventForm.get('patients')?.disable({ emitEvent: false });

    this.loadRolesWithPersonnel();
    this.appointmentService.appointmentStatus$
      .pipe(takeUntil(this.destroy$))
      .subscribe((statuses) => (this.appointmentStatusList = statuses));

    this.appointmentService.slotStatus$
      .pipe(takeUntil(this.destroy$))
      .subscribe((statuses) => (this.slotStatusList = statuses));

    this.appointmentService.participantStatus$
      .pipe(takeUntil(this.destroy$))
      .subscribe((statuses) => (this.participantStatusList = statuses));

    this.calendarEventService.addCalendarEventModalSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((showModal) => {
        if (!showModal) return;

        const modalData = this.calendarEventService.addCalendarEventModalData;
        this.isVisible = true;
        this.isTitleEditing = false;
        this.isTitleManuallyEdited = !!modalData.event;
        this.isAutoTitleEnabled = !modalData.event;
        this.committedTitle = '';

        this.patientsList = [];
        this.allowedPatientIds.clear();

        if (modalData.personnel) {
          this.personnelList = (
            Array.isArray(modalData.personnel) ? modalData.personnel : []
          ).map((resource: any) => ({
            userId: resource.id, // Assuming `id` in ResourceInput maps to `userId`
            firstName: resource.title?.split(' ')[0] || '', // Extract first name from title
            lastName: resource.title?.split(' ')[1] || '', // Extract last name from title
          }));
        }

        if (modalData.departments) {
          this.departmentList = Array.isArray(modalData.departments)
            ? modalData.departments
            : [];
        }

        if (modalData.recurrentType) {
          this.recurrenceTypeList = Array.isArray(modalData.recurrentType)
            ? modalData.recurrentType
            : [];
        }
        if (modalData.appointmentServiceTypeList) {
          this.appointmentServiceTypeList = Array.isArray(
            modalData.appointmentServiceTypeList
          )
            ? modalData.appointmentServiceTypeList
            : [];
        }
        if (modalData.appointmentReasonList) {
          this.appointmentReasonsList = Array.isArray(
            modalData.appointmentReasonList
          )
            ? modalData.appointmentReasonList
            : [];
        }

        if (modalData.personnelId) {
          this.preselectedPersonnelIds = [modalData.personnelId];
          this.patchPersonnelSelection(this.preselectedPersonnelIds);
        }

        if (modalData.event) {
          this.eventForm.get('appointmentStatusId')?.enable(); // Enable appointmentStatusId for editing
          if (
            modalData.event?.extendedProps?.['recurrentTypeId'] &&
            modalData.event.extendedProps['recurrentTypeId'] !== 1
          ) {
            this.isEditingRecurring = true;
          }

          const event = modalData.event;
          this.editingEvent = event;

          this.patchAppointmentParticipantsForEdit(event);

          //  Patch Patients Correctly
          const selectedPatients = event.extendedProps?.['patients']?.map(
            (p: any) => String(p.id)
          );

          const startDate =
            event.start?.getFullYear() +
            '-' +
            String(event.start?.getMonth()! + 1).padStart(2, '0') +
            '-' +
            String(event.start?.getDate()).padStart(2, '0');
          const startTime = event.start?.toTimeString().slice(0, 5);
          const endTime = event.end
            ? event.end.toTimeString().slice(0, 5)
            : this.getNextTimeOption(startTime!);

          //  Patch Form Values
          this.eventForm.patchValue({
            id: Number(event.id),
            title: event.title,
            description: event.extendedProps?.['description'],
            startDate,
            startTime,
            endTime,
            allDay: event.allDay,
            eventColor: event.backgroundColor || 'blue',

            // Appointment & Slot Status
            appointmentStatusId:
              event.extendedProps?.['appointmentStatusId'] || null,
            appointmentServiceTypeId:
              event.extendedProps?.['appointmentServiceTypeId'] || null,
            appointmentReasonId:
              event.extendedProps?.['appointmentReasonId'] || null,
            slotStatus: event.extendedProps?.['slotStatusId'] || null,
            slotId: event.extendedProps?.['slotId'] || null,
            recurrenceFrequency: event.extendedProps?.['recurrentTypeId'] || 1,
            recurrenceOccurrences:
              event.extendedProps?.['recurrenceOccurrences'] || null,

            //  Patch Patients
            patients: selectedPatients.length > 0 ? selectedPatients : null,

            //  Patch Department
            department: this.departmentList
              .filter((dept) =>
                event.extendedProps?.['department']?.some(
                  (d: any) => d.id === dept.id
                )
              )
              .map((d) => d.id),

            // Patch Participant Status (Personnel, Patients, Departments)
            personnelStatus:
              event.extendedProps?.['personnel'][0]?.participantStatusId ||
              null,
            patientStatus:
              event.extendedProps?.['patients'][0]?.participantStatusId || null,
            departmentStatus:
              event.extendedProps?.['department'][0]?.participantStatusId ||
              null,
          });

          //  Ensure UI Updates
          this.onColorChange(event.backgroundColor || 'blue');
        } else if (modalData.start) {
          // 🔒 Disable appointmentStatusId for new appointments with prefilled start
          this.eventForm.get('appointmentStatusId')?.disable();
          this.eventForm.patchValue({ appointmentStatusId: 3 });
          const startDate =
            modalData.start.getFullYear() +
            '-' +
            String(modalData.start.getMonth() + 1).padStart(2, '0') +
            '-' +
            String(modalData.start.getDate()).padStart(2, '0');
          const startTime = modalData.start.toTimeString().slice(0, 5);
          const endTime = modalData.end
            ? modalData.end.toTimeString().slice(0, 5)
            : this.getNextTimeOption(startTime);

          // All-day event (00:00 to 00:00 | 23:59)
          // If startTime is 00:00 and endTime is 00:00 or 23:59, set allDay to true
          if (
            startTime === '00:00' &&
            (endTime === '00:00' || endTime === '23:59')
          ) {
            this.eventForm.patchValue({
              startDate: startDate,
              startTime: startTime,
              endTime: endTime,
              allDay: true,
            });

            // If we have an end date and it's not the same as start date, calculate recurrence
            if (modalData.end) {
              const startDateOnly = new Date(
                modalData.start.toISOString().split('T')[0]
              );
              const endDateOnly = new Date(
                modalData.end.toISOString().split('T')[0]
              );

              const diffInMs = endDateOnly.getTime() - startDateOnly.getTime();
              const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

              if (diffInDays > 1) {
                this.eventForm.patchValue({
                  recurrenceFrequency: 2, // Daily
                  recurrenceOccurrences: diffInDays,
                });
              }
            }
          } else {
            // Regular timed event
            this.eventForm.patchValue({
              startDate: startDate,
              startTime: startTime,
              endTime: endTime,
            });
          }
        } else {
          // 🔒 Disable appointmentStatusId for new appointments with no start (empty event)
          this.eventForm.get('appointmentStatusId')?.disable();
          this.eventForm.patchValue({ appointmentStatusId: 3 });
          // Prefill with today and now/+15m
          const now = new Date();
          const pad = (n: number) => n.toString().padStart(2, '0');
          // Round up to next 15 minutes
          let minutes = now.getMinutes();
          let roundedMinutes = Math.ceil(minutes / 15) * 15;
          if (roundedMinutes === 60) {
            now.setHours(now.getHours() + 1);
            roundedMinutes = 0;
          }
          now.setMinutes(roundedMinutes, 0, 0);

          const startTime = `${pad(now.getHours())}:${pad(now.getMinutes())}`;
          const end = new Date(now.getTime() + 15 * 60000);
          const endTime = `${pad(end.getHours())}:${pad(end.getMinutes())}`;
          const today = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
            now.getDate()
          )}`;

          this.eventForm.patchValue({
            startDate: today,
            startTime,
            endTime,
          });
        }

        this.refreshAllowedPatients(!!modalData.event);

        this.updateAutoGeneratedTitle();

        this.captureInitialFormState();
      });

    this.eventForm.get('allDay')?.valueChanges.subscribe((allDay: boolean) => {
      if (allDay) {
        this.startTimeVisible = false;
        this.endTimeVisible = false;
        this.eventForm.get('eventColor')?.enable(); // Enable color selection if allDay is true
        // Remove required validators for time fields
        this.eventForm.get('startTime')?.clearValidators();
        this.eventForm.get('endTime')?.clearValidators();
        this.eventForm.get('startTime')?.updateValueAndValidity();
        this.eventForm.get('endTime')?.updateValueAndValidity();
      } else {
        this.startTimeVisible = true;
        this.endTimeVisible = true;
        this.eventForm.get('eventColor')?.disable(); // Disable color selection if allDay is false
        // Re-apply required validators for time fields
        this.eventForm.get('startTime')?.setValidators([Validators.required]);
        this.eventForm.get('endTime')?.setValidators([Validators.required]);
        this.eventForm.get('startTime')?.updateValueAndValidity();
        this.eventForm.get('endTime')?.updateValueAndValidity();
        // If times are empty or "00:00", reset them to null to trigger validation
        if (
          !this.eventForm.get('startTime')?.value ||
          this.eventForm.get('startTime')?.value === '00:00'
        ) {
          this.eventForm.get('startTime')?.setValue(null);
        }
        if (
          !this.eventForm.get('endTime')?.value ||
          this.eventForm.get('endTime')?.value === '00:00'
        ) {
          this.eventForm.get('endTime')?.setValue(null);
        }
      }
      // Force status check
      this.eventForm.updateValueAndValidity({
        onlySelf: false,
        emitEvent: true,
      });
    });
    this.eventForm
      .get('roles')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.isPatchingEditParticipants) {
          return;
        }
        this.filterDataBySelection();
        this.eventForm.get('specialties')?.setValue([]); // Reset sub-roles when role changes
        this.eventForm.get('personnel')?.setValue([]); // Reset personnel
      });

    this.eventForm
      .get('specialties')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.isPatchingEditParticipants) {
          return;
        }
        this.filterDataBySelection();
        this.eventForm.get('personnel')?.setValue([]); // Reset personnel
      });

    this.eventForm
      .get('personnel')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.isPatchingEditParticipants) {
          this.refreshAllowedPatients(false);
        }
      });

    this.eventForm
      .get('patients')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updatePatientCompatibilityError());

    this.eventForm
      .get('allDay')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((allDay: boolean) => {
        if (allDay) {
          this.eventForm.get('eventColor')?.enable();
        } else {
          this.eventForm.get('eventColor')?.disable();
        }

        // Force status check
        this.eventForm.updateValueAndValidity({
          onlySelf: false,
          emitEvent: true,
        });
      });

    this.eventForm
      .get('end')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        // Force status check
        this.eventForm.updateValueAndValidity({
          onlySelf: false,
          emitEvent: true,
        });
      });

    this.eventForm
      .get('recurrenceFrequency')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((frequency: number) => {
        if (frequency === 1) {
          this.eventForm.get('recurrenceOccurrences')?.setValue(null);
        }
        this.eventForm.updateValueAndValidity();
      });

    this.eventForm
      .get('startTime')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((startTime: string) => {
        const index = this.timeOptions.indexOf(startTime);
        const next = this.timeOptions[index + 1] || this.timeOptions[index];
        this.eventForm.get('endTime')?.setValue(next);
      });

    ['appointmentServiceTypeId', 'appointmentReasonId', 'patients'].forEach(
      (controlName) => {
        this.eventForm
          .get(controlName)
          ?.valueChanges.pipe(takeUntil(this.destroy$))
          .subscribe(() => this.updateAutoGeneratedTitle());
      }
    );
  }

  get isPatientSelectionDisabled(): boolean {
    const selectedStaff = this.eventForm.get('personnel')?.value;
    return (
      this.isPatientOptionsLoading ||
      this.patientOptionsLoadFailed ||
      !Array.isArray(selectedStaff) ||
      selectedStaff.length === 0
    );
  }

  get patientSelectionTooltip(): string | null {
    const selectedStaff = this.eventForm.get('personnel')?.value;
    return !Array.isArray(selectedStaff) || selectedStaff.length === 0
      ? this.translate.instant(
          'Select at least one staff member to enable patient selection.',
        )
      : null;
  }

  private refreshAllowedPatients(preserveInvalidExistingPatients: boolean): void {
    const patientsControl = this.eventForm.get('patients');
    const selectedStaff = this.eventForm.get('personnel')?.value;
    const staffUserIds = Array.isArray(selectedStaff)
      ? selectedStaff.map(String)
      : [];
    const requestVersion = ++this.patientRequestVersion;

    if (staffUserIds.length === 0) {
      patientsControl?.setValue(null);
      patientsControl?.disable({ emitEvent: false });
      patientsControl?.setErrors(null);
      this.patientsList = [];
      this.allowedPatientIds.clear();
      this.isPatientOptionsLoading = false;
      this.patientOptionsLoadFailed = false;
      return;
    }

    patientsControl?.disable({ emitEvent: false });
    this.isPatientOptionsLoading = true;
    this.patientOptionsLoadFailed = false;
    this.patientsList = [];

    this.appointmentService
      .getAllowedPatientsForParticipants(staffUserIds)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (requestVersion !== this.patientRequestVersion) return;

          if (!response?.succeed || !response.data) {
            this.handlePatientOptionsLoadFailure(patientsControl);
            return;
          }

          this.isPatientOptionsLoading = false;
          this.patientOptionsLoadFailed = false;
          const patientErrors = { ...(patientsControl?.errors ?? {}) };
          delete patientErrors['patientOptionsUnavailable'];
          patientsControl?.setErrors(
            Object.keys(patientErrors).length > 0 ? patientErrors : null,
          );
          const allowedPatients = response.data.map((patient) => ({
            id: String(patient.id),
            title: patient.fullName,
            disabled: false,
          }));
          this.allowedPatientIds = new Set(
            allowedPatients.map((patient) => patient.id),
          );

          const selectedPatientIds = this.getSelectedPatientIds();
          const invalidPatientIds = selectedPatientIds.filter(
            (patientId) => !this.allowedPatientIds.has(patientId),
          );

          if (preserveInvalidExistingPatients && invalidPatientIds.length > 0) {
            const existingPatients = this.editingEvent?.extendedProps?.['patients'];
            const legacyOptions = invalidPatientIds.map((patientId) => {
              const existing = Array.isArray(existingPatients)
                ? existingPatients.find(
                    (patient: any) => String(patient.id) === patientId,
                  )
                : null;
              return {
                id: patientId,
                title:
                  existing?.title ||
                  this.translate.instant('Unavailable patient'),
                disabled: true,
              };
            });
            this.patientsList = [...allowedPatients, ...legacyOptions];
            patientsControl?.enable({ emitEvent: false });
            this.updatePatientCompatibilityError();
            return;
          }

          this.patientsList = allowedPatients;
          if (invalidPatientIds.length > 0) {
            patientsControl?.setValue(
              selectedPatientIds.filter((patientId) =>
                this.allowedPatientIds.has(patientId),
              ),
            );
            this.swalToastService.toastWarning(
              'Some selected patients were removed because they are not available to all selected staff members.',
            );
          }
          patientsControl?.enable({ emitEvent: false });
          this.updatePatientCompatibilityError();
          this.updateAutoGeneratedTitle();
        },
        error: () => {
          if (requestVersion !== this.patientRequestVersion) return;
          this.handlePatientOptionsLoadFailure(patientsControl);
        },
      });
  }

  private handlePatientOptionsLoadFailure(
    patientsControl: AbstractControl | null,
  ): void {
    this.isPatientOptionsLoading = false;
    this.patientOptionsLoadFailed = true;
    this.patientsList = [];
    this.allowedPatientIds.clear();
    patientsControl?.enable({ emitEvent: false });
    patientsControl?.setErrors({
      ...(patientsControl.errors ?? {}),
      patientOptionsUnavailable: true,
    });
    this.swalToastService.toastError('Unable to load available patients');
  }

  private getSelectedPatientIds(): string[] {
    const selectedPatients = this.eventForm.get('patients')?.value;
    return Array.isArray(selectedPatients) ? selectedPatients.map(String) : [];
  }

  private updatePatientCompatibilityError(): void {
    const patientsControl = this.eventForm.get('patients');
    if (!patientsControl || patientsControl.disabled) return;

    const hasInvalidPatient = this.getSelectedPatientIds().some(
      (patientId) => !this.allowedPatientIds.has(patientId),
    );
    const currentErrors = { ...(patientsControl.errors ?? {}) };

    if (hasInvalidPatient) {
      currentErrors['incompatibleStaffPatients'] = true;
      patientsControl.setErrors(currentErrors);
    } else {
      delete currentErrors['incompatibleStaffPatients'];
      patientsControl.setErrors(
        Object.keys(currentErrors).length > 0 ? currentErrors : null,
      );
    }
  }

  private updateAutoGeneratedTitle(): void {
    if (
      !this.isAutoTitleEnabled ||
      this.isTitleManuallyEdited ||
      this.isTitleEditing
    ) {
      return;
    }

    const serviceTypeId = this.eventForm.get('appointmentServiceTypeId')?.value;
    const reasonId = this.eventForm.get('appointmentReasonId')?.value;
    const serviceType = this.appointmentServiceTypeList.find(
      (item) => String(item.id) === String(serviceTypeId)
    )?.name?.trim();
    const reason = this.appointmentReasonsList.find(
      (item) => String(item.id) === String(reasonId)
    )?.name?.trim();

    let generatedTitle = '';
    if (serviceType && reason) {
      const selectedPatientIds = this.eventForm.get('patients')?.value;
      const patientIds = Array.isArray(selectedPatientIds)
        ? selectedPatientIds
        : [];

      if (patientIds.length === 0) {
        generatedTitle = `${serviceType} (${reason})`;
      } else if (patientIds.length === 1) {
        const patientName = this.patientsList.find(
          (patient) => String(patient.id) === String(patientIds[0])
        )?.title;
        if (patientName) {
          generatedTitle = `${serviceType} - ${patientName} (${reason})`;
        }
      } else {
        generatedTitle = `${serviceType} - ${patientIds.length} patients (${reason})`;
      }
    }

    this.committedTitle = generatedTitle;
    this.eventForm.get('title')?.setValue(generatedTitle || null, {
      emitEvent: false,
    });
  }

  editTitle(): void {
    this.committedTitle = this.eventForm.get('title')?.value || '';
    this.isTitleEditing = true;
  }

  saveTitle(): void {
    this.committedTitle = this.eventForm.get('title')?.value || '';
    this.isTitleManuallyEdited = true;
    this.isAutoTitleEnabled = false;
    this.isTitleEditing = false;
  }

  cancelTitleEdit(): void {
    this.eventForm.get('title')?.setValue(this.committedTitle || null);
    this.isTitleEditing = false;
  }

  onTitleMouseMove(event: MouseEvent): void {
    const textarea = event.currentTarget as HTMLTextAreaElement;
    const resizeGripSize = 18;
    const isOverResizeGrip =
      event.offsetX >= textarea.clientWidth - resizeGripSize &&
      event.offsetY >= textarea.clientHeight - resizeGripSize;

    this.titleTooltipVisible = !isOverResizeGrip;
  }

  loadRolesWithPersonnel(): void {
    this.appointmentService
      .getUsersByRoleDropdownData()
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        if (response && response.succeed && response.data) {
          this.rolesList = response.data; // Store full roles with users and sub-roles

          // Collect all sub-roles (specialties) from all roles
          this.specialtiesList = response.data.flatMap(
            (role) => role.subRoleUsers
          );

          // Collect all unique personnel (avoiding duplicates)
          const personnelSet = new Map<string, IPersonel>();

          response.data.forEach((role) => {
            role.users.forEach((user) => personnelSet.set(user.userId, user));
            role.subRoleUsers.forEach((subRole) => {
              subRole.users.forEach((user) =>
                personnelSet.set(user.userId, user)
              );
            });
          });

          this.personnelList = Array.from(personnelSet.values());

          if (this.isVisible && this.editingEvent) {
            this.patchAppointmentParticipantsForEdit(this.editingEvent);
            this.refreshAllowedPatients(true);
          } else if (this.isVisible && this.preselectedPersonnelIds.length) {
            this.patchPersonnelSelection(this.preselectedPersonnelIds);
            this.refreshAllowedPatients(false);
          }
        }
      });
  }

  private patchAppointmentParticipantsForEdit(
    event: EventApi | EventInput
  ): void {
    this.patchPersonnelSelection(this.getSelectedPersonnelIds(event));
  }

  // Selects the given users as personnel, together with the roles/specialties that list them
  private patchPersonnelSelection(selectedPersonnelIds: string[]): void {
    const shouldRefreshInitialState = this.eventForm.pristine;
    const selectedPersonnelIdSet = new Set(
      selectedPersonnelIds.map((id) => id.toLowerCase())
    );
    const selectedRoles = new Set<string>();
    const selectedSubRoles = new Set<string>();

    this.rolesList.forEach((role) => {
      if (
        role.users.some((user) =>
          selectedPersonnelIdSet.has(user.userId.toLowerCase())
        )
      ) {
        selectedRoles.add(role.roleId);
      }
      role.subRoleUsers.forEach((subRole) => {
        if (
          subRole.users.some((user) =>
            selectedPersonnelIdSet.has(user.userId.toLowerCase())
          )
        ) {
          selectedRoles.add(role.roleId);
          selectedSubRoles.add(subRole.roleId);
        }
      });
    });

    this.isPatchingEditParticipants = true;

    try {
      this.eventForm.patchValue(
        {
          roles: Array.from(selectedRoles),
          specialties: Array.from(selectedSubRoles),
        },
        { emitEvent: false }
      );

      this.filterDataBySelection();

      this.eventForm.patchValue(
        {
          personnel: this.filteredPersonnelList
            .filter((person) =>
              selectedPersonnelIdSet.has(person.userId.toLowerCase())
            )
            .map((person) => person.userId),
        },
        { emitEvent: false }
      );
    } finally {
      this.isPatchingEditParticipants = false;
    }

    if (shouldRefreshInitialState) {
      this.captureInitialFormState();
    }
  }

  private getSelectedPersonnelIds(event: EventApi | EventInput): string[] {
    const personnel = event.extendedProps?.['personnel'];

    if (!Array.isArray(personnel)) {
      return [];
    }

    return personnel
      .map((participant: any) =>
        participant?.id === null || participant?.id === undefined
          ? null
          : String(participant.id)
      )
      .filter((id: string | null | undefined): id is string => !!id);
  }

  filterDataBySelection(): void {
    const selectedRoles = this.eventForm.get('roles')?.value || [];
    const selectedSpecialties = this.eventForm.get('specialties')?.value || [];

    // Reset filtered lists
    this.filteredPersonnelList = [];
    this.filteredSpecialtiesList = [];

    if (selectedRoles.length === 0) {
      return; // If no role is selected, clear filters
    }

    const matchedPersonnel: IPersonel[] = [];

    //  Filter Subroles (Specialties) First
    this.filteredSpecialtiesList = this.rolesList
      .filter((role) => selectedRoles.includes(role.roleId)) // Get selected roles
      .flatMap((role) => role.subRoleUsers); // Extract their sub-roles

    //  Filter Personnel from the Selected Roles
    selectedRoles.forEach((roleId: string) => {
      const role = this.rolesList.find((r) => r.roleId === roleId);
      if (role) {
        matchedPersonnel.push(...role.users);
      }
    });

    //  Further filter Personnel from the Selected Sub-Roles
    selectedSpecialties.forEach((subRoleId: string) => {
      const subRole = this.specialtiesList.find((s) => s.roleId === subRoleId);
      if (subRole) {
        matchedPersonnel.push(...subRole.users);
      }
    });

    // Remove duplicate personnel
    this.filteredPersonnelList = Array.from(
      new Map(
        matchedPersonnel.map((person) => [person.userId, person])
      ).values()
    );

    //  Ensure UI updates by triggering Angular's change detection
    this.eventForm.get('personnel')?.updateValueAndValidity();
  }

  // Format date helper function
  formatDate(date: Date): string {
    const pad = (n: number) => (n < 10 ? '0' + n : n);

    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  onColorChange(selectedColor: string) {
    for (const color of this.colorOptions) {
      this.colorOpacity[color.name] = color.name === selectedColor ? 1 : 0.3;
    }
  }

  getTimeDiffLabel(endTime: string): string {
    const start = this.eventForm?.value?.startTime;
    if (!start || !endTime || endTime <= start) return '';

    const [startH, startM] = start.split(':').map(Number);
    const [endH, endM] = endTime.split(':').map(Number);

    const diffMins = endH * 60 + endM - (startH * 60 + startM);

    if (diffMins >= 60) {
      const hours = Math.floor(diffMins / 60);
      const minutes = diffMins % 60;
      return ` (${hours}${this.translate.instant('h')}${
        minutes > 0 ? `${minutes}${this.translate.instant('m')}` : ''
      })`;
    }

    return ` (${diffMins}${this.translate.instant('m')})`;
  }

  getNextTimeOption(startTime: string): string {
    const index = this.timeOptions.indexOf(startTime);
    return (
      this.timeOptions[index + 1] ||
      this.timeOptions[this.timeOptions.length - 1]
    );
  }

  private getCarePlanPeriodEnd(): Date | null {
    const periodEnd =
      this.calendarEventService.addCalendarEventModalData.carePlanPeriodEnd;

    return periodEnd ? new Date(periodEnd) : null;
  }

  private getProjectedLastSlotEnd(eventData: EventInput): Date | null {
    const end = this.toDateValue(eventData.end);
    if (!end) {
      return null;
    }

    const frequency = Number(this.eventForm.get('recurrenceFrequency')?.value ?? 1);
    const occurrences = Number(
      this.eventForm.get('recurrenceOccurrences')?.value ?? 0,
    );

    if (frequency === 1 || occurrences <= 0) {
      return end;
    }

    const projectedEnd = new Date(end);

    for (let index = 0; index < occurrences; index += 1) {
      switch (frequency) {
        case 2:
          projectedEnd.setDate(projectedEnd.getDate() + 1);
          break;
        case 3:
          projectedEnd.setDate(projectedEnd.getDate() + 7);
          break;
        case 4:
          projectedEnd.setMonth(projectedEnd.getMonth() + 1);
          break;
        case 5:
          projectedEnd.setFullYear(projectedEnd.getFullYear() + 1);
          break;
        default:
          return end;
      }
    }

    return projectedEnd;
  }

  private toDateValue(value: EventInput['end']): Date | null {
    if (!value) {
      return null;
    }

    if (value instanceof Date) {
      return new Date(value);
    }

    if (typeof value === 'string' || typeof value === 'number') {
      return new Date(value);
    }

    return null;
  }

  private getCarePlanBoundaryMessage(eventData: EventInput): string | null {
    const carePlanPeriodEnd = this.getCarePlanPeriodEnd();
    if (!carePlanPeriodEnd) {
      return null;
    }

    const projectedLastSlotEnd = this.getProjectedLastSlotEnd(eventData);
    if (!projectedLastSlotEnd) {
      return null;
    }

    return projectedLastSlotEnd > carePlanPeriodEnd
      ? this.translate.instant(
          'The care plan end date cannot be before one or more linked appointment slots.',
        )
      : null;
  }

  disabledPastDates = (current: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return current < today;
  };
  generateTimeOptions(): string[] {
    const times: string[] = [];
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    for (let i = 0; i < 24 * 4; i++) {
      const h = start.getHours().toString().padStart(2, '0');
      const m = start.getMinutes().toString().padStart(2, '0');
      times.push(`${h}:${m}`);
      start.setMinutes(start.getMinutes() + 15);
    }
    return times;
  }

  onStartTimeChange(): void {
    const startTime = this.eventForm.get('startTime')?.value;
    const index = this.timeOptions.indexOf(startTime);
    const next = this.timeOptions[index + 1] || this.timeOptions[index]; // fallback
    this.eventForm.get('endTime')?.setValue(next);
  }

  combineDateAndTime(date: string | Date, time: string): string {
    let dateString: string;

    if (typeof date === 'string') {
      dateString = date;
    } else if (date instanceof Date) {
      // Convert to YYYY-MM-DD format using local time
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      dateString = `${year}-${month}-${day}`;
    } else {
      throw new Error('Invalid date input');
    }

    return `${dateString}T${time}:00`;
  }

  private buildEventDataFromForm(isEditing: boolean): EventInput {
    const formValue = this.eventForm.value;

    // Extract existing participants from the event being edited (if any)
    const existingPersonnel = isEditing
      ? this.editingEvent?.extendedProps?.['personnel'] || []
      : [];
    const existingPatients = isEditing
      ? this.editingEvent?.extendedProps?.['patients'] || []
      : [];
    const existingDepartments = isEditing
      ? this.editingEvent?.extendedProps?.['department'] || []
      : [];

    const selectedPersonnel = this.personnelList
      .filter((personnel) => formValue.personnel.includes(personnel.userId))
      .map((personnel) => {
        const existing = existingPersonnel.find(
          (p: any) => p.id === personnel.userId
        );
        return {
          id: personnel.userId,
          participantId: existing ? existing.participantId : null, //  Keep existing participantId, set to null if new
          actorId: existing ? existing.actorId : null, //  Keep existing actorId, set to null if new
          title: personnel.firstName + ' ' + personnel.lastName,
          participantStatusId: formValue.personnelStatus,
        };
      });

    const selectedPatients = this.patientsList
      .filter((patient) =>
        Array.isArray(formValue.patients)
          ? formValue.patients.includes(String(patient.id))
          : false
      ) // Convert patient.id to string for safe comparison
      .map((patient) => {
        const existing = existingPatients.find(
          (p: any) => String(p.id) === String(patient.id)
        );
        return {
          id: existing ? existing.id : patient.id,
          participantId: existing ? existing.participantId : null, //  Keep existing participantId, set to null if new
          actorId: existing ? existing.actorId : null, //  Keep existing actorId, set to null if new
          title: patient.title,
          participantStatusId: formValue.patientStatus,
        };
      });

    let selectedDepartments;
    if (formValue.department) {
      selectedDepartments = this.departmentList
        .filter((department) => formValue.department.includes(department.id))
        .map((department) => {
          const existing = existingDepartments.find(
            (d: any) => d.id === department.id
          );
          return {
            id: existing ? existing.participantId : null, //  Keep existing participantId, set to null if new
            participantId: existing ? existing.participantId : null,
            actorId: existing ? existing.actorId : null,
            title: department.title,
            participantStatusId: formValue.departmentStatus,
          };
        });
    }

    const startDateTime = this.combineDateAndTime(
      this.eventForm.value.startDate,
      this.eventForm.value.startTime
    );
    const endDateTime = this.combineDateAndTime(
      this.eventForm.value.startDate, // startDate is same as endDate
      this.eventForm.value.endTime
    );

    const eventData = {
      id: formValue.id,
      resourceIds: formValue.patients,
      title: formValue.title,
      start: new Date(startDateTime),
      end: new Date(endDateTime),
      allDay: formValue.allDay,
      extendedProps: {
        description: formValue.description,
        department: selectedDepartments,
        patients: selectedPatients,
        personnel: selectedPersonnel,
        appointmentStatusId: formValue.appointmentStatusId,
        appointmentServiceTypeId: formValue.appointmentServiceTypeId,
        appointmentReasonId: formValue.appointmentReasonId,
        slotId: formValue.slotId, // all recurring slots here!
        recurrentTypeId: formValue.recurrenceFrequency,
        recurrenceOccurrences: formValue.recurrenceOccurrences,
      },
      borderColor: formValue.eventColor,
      backgroundColor: formValue.eventColor,
    };
    return eventData;
  }

  private proceedWithEventSave(eventData: EventInput, isEditing: boolean) {
    const onUpdateSuccess =
      this.calendarEventService.addCalendarEventModalData.onUpdateSuccess;

    if (isEditing) {
      const updateType = this.isEditingRecurring
        ? this.eventForm.get('updateOption')?.value
        : 0;

      if (onUpdateSuccess) {
        this.isLoading = true;
        const backendData = mapCalendarEventsToBackendAppointmentData(
          eventData,
          this.rolesList
        );

        this.appointmentService
          .updateAppointment(String(backendData.id), backendData, updateType || undefined)
          .subscribe({
            next: () => {
              this.isLoading = false;
              onUpdateSuccess(backendData.id, eventData);
              this.eventForm.markAsPristine();
              this.handleCancel();
            },
            error: (err) => {
              this.isLoading = false;
              this.modalService.error({
                nzTitle:
                  err?.error?.message ||
                  this.translate.instant('Unable to update appointment'),
              });
            },
          });
        return;
      }

      this.calendarEventService.updateEvent(eventData, updateType);
      this.eventForm.markAsPristine();
      this.handleCancel();
      return;
    }

    const backendData = mapCalendarEventsToBackendAppointmentData(
      eventData,
      this.rolesList
    );
    const onCreateSuccess =
      this.calendarEventService.addCalendarEventModalData.onCreateSuccess;

    if (onCreateSuccess) {
      this.isLoading = true;
      this.appointmentService.newAppointment(backendData).subscribe({
        next: (response) => {
          this.isLoading = false;
          onCreateSuccess(response.data, eventData);
          this.eventForm.markAsPristine();
          this.handleCancel();
        },
        error: (err) => {
          this.isLoading = false;
          this.modalService.error({
            nzTitle:
              err?.error?.message ||
              this.translate.instant('Unable to create appointment'),
          });
        },
      });
    } else {
      this.calendarEventService.addEvent(eventData);
      this.eventForm.markAsPristine();
      this.handleCancel();
    }
  }

  private askUserToConfirmConflict(
    conflictData: IAppointmentOverlapsData,
    onConfirm: () => void
  ) {
    const formatISO = (iso: string): { date: string; time: string } => {
      const [rawDate, timeWithZone] = iso.split('T');
      const [year, month, day] = rawDate.split('-');
      const [hour, minute] = timeWithZone.replace('Z', '').split(':');
      return {
        date: `${day}/${month}/${year}`,
        time: `${hour}:${minute}`,
      };
    };

    const conflictText = conflictData.conflicts
      .map((c) => {
        const start = formatISO(c.start);
        const end = formatISO(c.end);
        return `
        <div style="margin-bottom: 18px; text-align: left;">
          <div><b>${this.translate.instant('Slot')}:</b></div>
          <div style="margin-left: 16px; margin-top: 6px;">
            <div><b>${this.translate.instant('Start')}:</b> ${start.date}, ${
          start.time
        }</div>
            <div><b>${this.translate.instant('End')}:</b> ${end.date}, ${
          end.time
        }</div>
          </div>
          <br>
          <div><b>${this.translate.instant('Participants')}:</b></div>
          <div style="margin-left: 16px; margin-top: 6px;">
            ${c.conflictingNames.map((name) => `<div>${name}</div>`).join('')}
          </div>
        </div>
      `;
      })
      .join('<hr style="border-top: 1px solid #eee;" />');

    Swal.fire({
      title: this.translate.instant('Conflict Detected'),
      html: `
        <p>${this.translate.instant('Overlapping appointments found')}:</p>
        ${conflictText}
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: this.translate.instant('Proceed anyway'),
      confirmButtonColor: COLORS.green,
      cancelButtonText: this.translate.instant('Cancel'),
      width: 600,
    }).then((result) => {
      if (result.isConfirmed) {
        onConfirm();
      }
    });
  }

  // Handle form submission
  submitForm(): void {
    if (this.eventForm.valid) {
      const isEditing = !!this.editingEvent;
      const eventData = this.buildEventDataFromForm(isEditing);
      const carePlanBoundaryMessage = this.getCarePlanBoundaryMessage(eventData);

      if (carePlanBoundaryMessage) {
        this.modalService.error({
          nzTitle: carePlanBoundaryMessage,
        });
        return;
      }

      const backendData = mapCalendarEventsToBackendAppointmentData(
        eventData,
        this.rolesList
      );

      this.isLoading = true;
      this.appointmentService
        .checkForConflicts(backendData, eventData.id)
        .subscribe({
          next: (response) => {
            this.isLoading = false;
            const conflictData = response.data;

            if (
              !conflictData.overbooked &&
              conflictData.conflicts.length === 0
            ) {
              this.proceedWithEventSave(eventData, isEditing);
            } else {
              this.askUserToConfirmConflict(conflictData, () => {
                this.proceedWithEventSave(eventData, isEditing);
              });
            }
          },
          error: () => {
            this.isLoading = false;
            this.translate
              .get('Unable to check for conflicts')
              .subscribe((msg) => {
                this.modalService.error({ nzTitle: msg });
              });
          },
        });
    }
  }

  handleCancel(): void {
    if (this.eventForm.dirty && this.hasUnsavedChanges()) {
      this.showDiscardConfirm = true;
    } else {
      this.resetFormAndClose();
    }
  }

  confirmDiscard(): void {
    this.resetFormAndClose();
  }

  cancelDiscard(): void {
    this.showDiscardConfirm = false;
  }

  private captureInitialFormState(): void {
    this.initialFormState = this.serializeFormState(
      this.eventForm.getRawValue()
    );
  }

  private hasUnsavedChanges(): boolean {
    return (
      this.serializeFormState(this.eventForm.getRawValue()) !==
      this.initialFormState
    );
  }

  private serializeFormState(value: unknown): string {
    return JSON.stringify(this.normalizeFormValue(value));
  }

  private normalizeFormValue(value: unknown): unknown {
    if (value === null || value === undefined) return null;
    if (value instanceof Date) {
      if (
        value.getHours() === 0 &&
        value.getMinutes() === 0 &&
        value.getSeconds() === 0 &&
        value.getMilliseconds() === 0
      ) {
        const pad = (part: number) => String(part).padStart(2, '0');
        return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(
          value.getDate()
        )}`;
      }
      return value.toISOString();
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return null;
      if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
      if (/^\d{4}-\d{2}-\d{2}T.*$/.test(trimmed)) {
        const date = new Date(trimmed);
        if (!Number.isNaN(date.getTime())) return date.toISOString();
      }
      return trimmed;
    }

    if (Array.isArray(value)) {
      const normalized = value
        .map((item) => this.normalizeFormValue(item))
        .filter((item) => item !== null)
        .sort((left, right) =>
          JSON.stringify(left).localeCompare(JSON.stringify(right))
        );
      return normalized.length ? normalized : null;
    }

    if (typeof value === 'object') {
      return Object.keys(value as Record<string, unknown>)
        .sort()
        .reduce<Record<string, unknown>>((normalized, key) => {
          normalized[key] = this.normalizeFormValue(
            (value as Record<string, unknown>)[key]
          );
          return normalized;
        }, {});
    }

    return value;
  }

  getDefaultFormValues() {
    return {
      id: 0,
      title: null,
      description: null,
      department: null,
      patients: null,
      personnel: [],
      roles: [],
      specialties: [],
      startDate: null,
      startTime: null,
      endTime: null,
      allDay: false,
      eventColor: {
        value: this.colorOptions.find((color) => color.name === 'blue')?.value,
        disabled: true,
      },
      appointmentStatusId: 3, // Default status = booked
      appointmentServiceTypeId: null,
      appointmentReasonId: null,
      slotStatus: 21,
      slotId: 0,
      personnelStatus: 30,
      patientStatus: 30,
      departmentStatus: 30,
      recurrenceFrequency: 1,
      recurrenceOccurrences: null,
      updateOption: 3, // Default to "All slots" (id: 3) // Revert to 1
    };
  }

  private resetFormAndClose(): void {
    this.isAutoTitleEnabled = false;
    this.isTitleEditing = false;
    this.isTitleManuallyEdited = false;
    this.titleTooltipVisible = false;
    this.committedTitle = '';
    this.eventForm.reset(this.getDefaultFormValues());
    this.eventForm.get('patients')?.disable({ emitEvent: false });
    this.patientsList = [];
    this.allowedPatientIds.clear();
    this.patientRequestVersion += 1;
    this.isPatientOptionsLoading = false;
    this.patientOptionsLoadFailed = false;
    this.eventForm.get('appointmentStatusId')?.enable();
    this.calendarEventService.addCalendarEventModalSub.next(false);
    this.calendarEventService.addCalendarEventModalData = {};
    this.showDiscardConfirm = false;
    this.isVisible = false;
    this.editingEvent = null;
    this.preselectedPersonnelIds = [];
    this.isEditingRecurring = false;
    this.initialFormState = '';
    this.modalRef.destroy();
  }

  getRecurrenceTypeName(id: number): string {
    switch (id) {
      case 1:
        return this.translate.instant('Does not repeat');
      case 2:
        return this.translate.instant('days');
      case 3:
        return this.translate.instant('weeks');
      case 4:
        return this.translate.instant('months');
      case 5:
        return this.translate.instant('years');
      default:
        return this.translate.instant('Unknown recurrence type');
    }
  }

  get examplePlaceholder() {
    return this.translate.instant('e.g') + '., 5';
  }
}
